<template>
  <div class="json-editor">
    <JsonEditor
      v-model="editorValue"
      v-bind="editorProps"
      height="300"
    />
  </div>
</template>

<script lang="ts">
import { GroupMetadata } from '@policyfly/protobuf/pfbingo'
import { defineComponent, computed, watch, ref } from 'vue'
import JsonEditor from 'vue3-ts-jsoneditor'

import type { JsonValue } from '@protobuf-ts/runtime'
import type { PropType } from 'vue'
import type { ValidationError } from 'vue3-ts-jsoneditor'

export default defineComponent({
  name: 'GroupMetadataField',

  components: {
    JsonEditor,
  },

  emits: {
    'update:modelValue': (value: GroupMetadata | null) => true,
  },

  props: {
    modelValue: {
      type: Object as PropType<GroupMetadata | null>,
      default: null,
    },
  },

  setup (props, { emit, expose }) {
    const internalValue = ref<GroupMetadata>(GroupMetadata.create())
    const editorValue = computed({
      get: () => GroupMetadata.toJson(internalValue.value),
      set: (value: JsonValue) => {
        try {
          internalValue.value = GroupMetadata.fromJson(value)
          emit('update:modelValue', internalValue.value)
        } catch {
          // ignore invalid json, validation will display error
          console.warn('Failed to parse JSON input')
        }
      },
    })

    const editorErrors = ref<ValidationError[]>([])
    const editorValid = computed(() => editorErrors.value.length === 0)
    const editorProps = {
      mainMenuBar: false,
      navigationBar: false,
      statusBar: false,
      validator: (json: unknown) => {
        const errors: ValidationError[] = []
        try {
          const group = GroupMetadata.fromJson(json as string)
          if (!GroupMetadata.is(group)) {
            errors.push({
              message: 'GroupMetadata does not match Protobuf schema.',
              path: [],
              severity: 'error' as ValidationError['severity'],
            })
          }
        } catch (e) {
          errors.push({
            message: `Invalid JSON: ${(e as Error).message}`,
            path: [],
            severity: 'error' as ValidationError['severity'],
          })
        }
        editorErrors.value = errors
        return errors
      },
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        internalValue.value = newValue ?? GroupMetadata.create()
      },
      { immediate: true },
    )

    expose({
      valid: editorValid,
    })

    return {
      editorProps,
      editorValue,
    }
  },
})
</script>
